import PropTypes from 'prop-types';
import React from 'react';
import { EllipsisVertical, GripVertical } from 'lucide-react';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@dashboard/components/ui/dropdown-menu';
import Tooltip from '@dashboard/components/Tooltip';
import clsx from 'clsx';

import { duplicateBlockedReason, fieldLabelText, ROOT_CONTAINER } from '@repo/lib';
import { getElementFieldTypeIcon } from '@dashboard/services/pageFieldTypes';

import { Button as ShadcnButton, buttonVariants } from '@dashboard/components/ui/button';
import {
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@dashboard/components/ui/accordion';
import { Badge } from '@dashboard/components/ui/badge';

import { useFieldEdit } from '../FieldEditContext';
import { useFieldHelpers } from '../useFieldHelpers';
import { SINGLE_MODE, elementForMode, layoutForField } from '../selectionRules';
import FieldEdit from './FieldEdit';
import MoveToMenu from './MoveToMenu';
import { useFormLayout } from '../FormLayoutContext';
import { useFieldSourceSettings } from '../FieldSourceContext';

const propTypes = {
  field: PropTypes.object.isRequired,
  dragHandleProps: PropTypes.object.isRequired,
  disabled: PropTypes.bool,
  containerId: PropTypes.string,
};

const FieldItem = ({ field, dragHandleProps, disabled, containerId = ROOT_CONTAINER }) => {
  const fieldId = field._id || field.key;

  if (!fieldId) {
    return null;
  }

  const { showRemoveField, onRemoveField, onToggleDisabled } = useFieldHelpers(fieldId);
  const { isAddingField } = useFieldEdit();
  const { duplicateField, groupField } = useFormLayout();
  const { layoutBlocks, isLocked, isUndeletable = isLocked } = useFieldSourceSettings();
  const locked = isUndeletable(field);
  const removeBlockedReason = locked
    ? 'Email address is asked for every attendee'
    : 'A page needs an email field';
  const duplicateBlocked = duplicateBlockedReason(field);
  const isDisabled = field.disabled === true;

  return (
    <AccordionItem value={fieldId} className="border-b-0">
      <div className="sticky top-0 bg-white dark:bg-background z-10">
        <div className="flex flex-row items-center gap-2 px-1">
          <div
            {...dragHandleProps}
            className={clsx(
              buttonVariants({ variant: 'ghost', size: 'icon' }),
              'cursor-grab active:cursor-grabbing',
            )}
          >
            <GripVertical className="size-4" />
          </div>

          <div className="flex-1 min-w-0">
            <AccordionTrigger
              data-ai-focus={`field:${fieldId}`}
              className={clsx('py-3 min-w-0 items-center hover:no-underline', {
                'cursor-not-allowed': isAddingField,
              })}
              disabled={field.isNew}
            >
              <div className="flex min-w-0 gap-x-2 items-center">
                <div className="shrink-0 text-foreground">
                  {getElementFieldTypeIcon(
                    field.element === 'multi-select'
                      ? elementForMode(SINGLE_MODE, layoutForField(field))
                      : field.element,
                  )}
                </div>

                <div className={clsx('truncate min-w-0', { 'opacity-60': isDisabled })}>
                  {fieldLabelText(field.label)} <span>{field.required ? '*' : ''}</span>
                  {isDisabled && (
                    <Tooltip label="Disabled fields are not shown to customers">
                      <Badge variant="secondary" className="ml-2">
                        Disabled
                      </Badge>
                    </Tooltip>
                  )}
                  {field.hidden && (
                    <Badge variant="secondary" className="ml-2">
                      Hidden
                    </Badge>
                  )}
                  {field.showHideLogic?.enabled && (
                    <Badge variant="secondary" className="ml-2">
                      Conditional
                    </Badge>
                  )}
                </div>
              </div>
            </AccordionTrigger>
          </div>

          <DropdownMenu>
            <DropdownMenuTrigger asChild disabled={isAddingField}>
              <ShadcnButton
                variant="ghost"
                size="icon"
                aria-label="More options"
                disabled={isAddingField}
              >
                <EllipsisVertical className="size-4" />
              </ShadcnButton>
            </DropdownMenuTrigger>

            <DropdownMenuContent align="end">
              {layoutBlocks && (
                <>
                  <MoveToMenu
                    itemKey={`field:${fieldId}`}
                    currentContainerId={containerId}
                    disabled={field.isNew}
                  />
                  {containerId === ROOT_CONTAINER && (
                    <DropdownMenuItem disabled={field.isNew} onSelect={() => groupField(field._id)}>
                      Group
                    </DropdownMenuItem>
                  )}
                  {duplicateField && (
                    <DropdownMenuItem
                      disabled={field.isNew || !!duplicateBlocked}
                      title={duplicateBlocked ?? undefined}
                      onSelect={() => duplicateField(field._id)}
                    >
                      Duplicate
                    </DropdownMenuItem>
                  )}
                </>
              )}
              <DropdownMenuItem
                disabled={field.isNew || !showRemoveField}
                title={
                  showRemoveField
                    ? undefined
                    : locked
                      ? removeBlockedReason
                      : "The customer's email field can't be disabled"
                }
                onSelect={onToggleDisabled}
              >
                {isDisabled ? 'Enable' : 'Disable'}
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                onSelect={onRemoveField}
                variant="destructive"
                disabled={!showRemoveField}
                title={showRemoveField ? undefined : removeBlockedReason}
              >
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        {field.error && (
          <div
            style={{
              marginTop: '0.5em',
              color: 'var(--gray-darker)',
            }}
          >
            {field.error}
          </div>
        )}
      </div>

      <AccordionContent
        className={clsx('pb-4 pt-2 relative', { 'px-3': containerId !== ROOT_CONTAINER })}
      >
        <FieldEdit field={field} disabled={disabled} fieldKey={field._id || field.key} />
      </AccordionContent>
    </AccordionItem>
  );
};

FieldItem.propTypes = propTypes;

export default FieldItem;
